/* istanbul ignore file */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useLayoutEffect, useEffect } from 'react';
import { Divider, Box } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { moduleClientFactory } from '../../../api-service/module-client-factory';
import { useApi } from '../../../hooks/use-api';
import { LookupAutocomplete } from '../components/lookup-autocomplete';
import { IContactPersonLookup, IContactPersonLookupProps } from './interfaces';
import { formatContactPersonValue, renderContactPerson } from './formatters';
import { useFetch } from './useFetch';
import { ReadonlyInput } from '../../readonly-input/readonly-input';
import { GetContactPersonByIdResponse } from '../../../api-service/clients';

const CREATE_NEW_CONTACT_PERSON_ID = 'CREATE_NEW_CONTACT_PERSON';
const DEFAULT_CREATE_NEW_LABEL = 'Opret ny kontaktperson';
const NO_OPTIONS_SEARCH_HINT = 'Skriv for at søge';

export function ContactPersonLookup(props: IContactPersonLookupProps) {
  const {
    onChange,
    value,
    fetchMinimumInputLength = 2,
    label = 'Kontaktperson',
    placeholder = 'Fremsøg eller opret kontaktperson',
    readOnly,
    onContactPersonSelected,
    onCreateNew,
    onOpenEntity,
    createNewLabel = DEFAULT_CREATE_NEW_LABEL,
    ...rest
  } = props;

  const [selectedValue, setSelectedValue] = useState<IContactPersonLookup>({} as IContactPersonLookup);
  const [searchValue, setSearchValue] = useState('');

  const createNewOption: IContactPersonLookup = {
    id: CREATE_NEW_CONTACT_PERSON_ID,
    name: createNewLabel,
  };

  function handleOnChange(selected: IContactPersonLookup) {
    if (selected?.id === CREATE_NEW_CONTACT_PERSON_ID) {
      onCreateNew?.();
      setSelectedValue(prev => (prev && prev.id !== CREATE_NEW_CONTACT_PERSON_ID ? { ...prev } : prev ?? ({} as IContactPersonLookup)));
      return;
    }
    onChange && onChange(selected);
  }

  function getOptionLabel(option?: IContactPersonLookup): string {
    if (option?.id === CREATE_NEW_CONTACT_PERSON_ID) return createNewLabel;
    return formatContactPersonValue(option);
  }

  function renderOption(optionProps: React.HTMLAttributes<HTMLLIElement>, option?: IContactPersonLookup): React.ReactNode {
    if (option?.id === CREATE_NEW_CONTACT_PERSON_ID) {
      return (
        <React.Fragment key={option.id}>
          <Divider />
          <Box
            component="li"
            {...optionProps}
            sx={{
              '&:hover': { backgroundColor: 'action.hover' },
            }}
          >
            <FontAwesomeIcon icon={faPlus} style={{ marginRight: 8 }} />
            {createNewLabel}
          </Box>
        </React.Fragment>
      );
    }
    return renderContactPerson(optionProps, option);
  }

  const lookupsClient = moduleClientFactory.createLookupContactPersonClient();
  const lookupSingleEntityApi = useApi((cancelToken, contactPersonId) => lookupsClient.getContactPersonById({ contactPersonId }, cancelToken), {} as GetContactPersonByIdResponse);

  useLayoutEffect(() => {
    if (!value) {
      setSelectedValue({} as IContactPersonLookup);
      return;
    }
    async function getSingle() {
      const response = await lookupSingleEntityApi.invoke(value);
      if (response.result.contactPerson) {
        setSelectedValue(response.result.contactPerson);
      }
    }
    getSingle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    onContactPersonSelected && onContactPersonSelected(selectedValue.name || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValue]);

  const fetchParams = { input: searchValue, fetchMinimumInputLength, maxResultSize: 15 };
  const { data, loading } = useFetch(fetchParams);

  const showNativeNoOptions = onCreateNew && data.length === 0;
  const noOptionsContent = showNativeNoOptions ? (
    <Box sx={{ py: 0, px: 0, margin: -1 }}>
      <Box sx={{ px: 2, py: 1.5, color: 'text.secondary' }}>{NO_OPTIONS_SEARCH_HINT}</Box>
      <Divider />
      <Box
        component="div"
        role="button"
        tabIndex={0}
        onClick={e => {
          e.stopPropagation();
          onCreateNew?.();
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            onCreateNew?.();
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1.5,
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        <FontAwesomeIcon icon={faPlus} style={{ marginRight: 8 }} />
        {createNewLabel}
      </Box>
    </Box>
  ) : undefined;

  let options: IContactPersonLookup[];
  if (showNativeNoOptions) {
    options = [];
  } else if (onCreateNew) {
    options = [...data, createNewOption];
  } else {
    options = data;
  }

  const displayValue = selectedValue?.id === CREATE_NEW_CONTACT_PERSON_ID ? ({} as IContactPersonLookup) : selectedValue;

  return readOnly ? (
    <ReadonlyInput label={label} value={formatContactPersonValue(selectedValue)} />
  ) : (
    <LookupAutocomplete<IContactPersonLookup>
      {...rest}
      label={label}
      placeholder={placeholder}
      isOptionEqualToValue={(option, val) => option?.id === val?.id}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      onChange={handleOnChange as never}
      value={displayValue}
      onSearchValueChange={setSearchValue}
      data={options}
      isLoading={loading}
      noOptionsText={noOptionsContent}
      onOpenEntity={onOpenEntity}
    />
  );
}
